// 工程标注（公差文本 / 形位公差符号）—— **前端单一出处**
//
// 为什么要有这个文件：公差文本、符号字形以前散在 FeatureTable 里各写一遍
// （`tolText()` 一套、展开行又一套），改一处漏一处。用户 2026-10-03 反馈的
// 「展开行改上下偏差、公差一栏不跟着变」正是这种双出处 + 派生优先级写反造成的。
// 现在：**公差列显示什么、展开行符号选什么，都只从这里取**。
//
// ---------------------------------------------------------------- 来源与口径
// 形位公差符号/中文名/是否需要基准/是否可用实体条件，逐条对照
// **ASME Y14.5-2018 图 6-1（几何特征符号）与图 6-12（修饰符）**，
// 工作区里的机读版为 `ASME_Y14.5-2018_资料/asme_y14.5_symbols.json`
// （`authority` 段注明「逐格视觉核对」）。本文件是它的**工程子集**：
// 只保留界面选择器需要的字段，符号串按「图纸上真实印出来的字形」录。
//
// ⚠️ 与识别引擎的关系（勿混）：
//   `backend/core_engine/config.py: GDT_SYMBOLS` 是**识别词典**（把抽取/OCR 得到的
//   字符映射成 symbol type），它沿用一批 Miscellaneous Technical 码位（如 ⌖ U+2316
//   当位置度）。本表是**录入与展示用**的标准字形，两者**不要求字符集相同**：
//   识别产出走 `symbolToZh()` 兼容，人工录入写标准字形。
//   报告侧读名走 `backend/report/common.py: GDT_SYMBOL_NAMES`，两边必须同时覆盖
//   （已在那边补齐 ✓ 见该文件注释）。

/** 形位公差的几何特征类别（ASME Y14.5-2018 图 6-1 分组）。 */
export type GdtCategory = 'form' | 'profile' | 'orientation' | 'location' | 'runout';

export interface GdtSym {
  /** 写库字符（features.gdt.symbol / gdt_features.symbol）—— 图纸上的标准字形。 */
  code: string;
  zh: string;
  en: string;
  category: GdtCategory;
  /** 是否需要（允许）基准参考 —— 形状公差不引用基准。 */
  datumAllowed: boolean;
  /** 公差值是否可用 Ⓜ/Ⓛ 实体条件。 */
  mmcAllowed: boolean;
  /** Y14.5-2018 已取消，仍见于旧图 / GB/T 1182。 */
  deprecated?: boolean;
  /** 选择器里的补充说明（标准原文要点，帮助选对符号）。 */
  note?: string;
}

/** 图 6-1 的 12 个在册几何特征（按类别顺序，与标准图表一致）。 */
export const GDT_SYMBOLS: GdtSym[] = [
  // ---- 形状 form（不引用基准）
  { code: '\u23e4', zh: '直线度', en: 'Straightness', category: 'form',
    datumAllowed: false, mmcAllowed: true,
    note: '线素直线度：指引线指向表面；导出中位线直线度：接尺寸线，公差值前加 ⌀' },
  { code: '\u25b1', zh: '平面度', en: 'Flatness', category: 'form',
    datumAllowed: false, mmcAllowed: true,
    note: '▱ = 平行四边形，不是矩形；可基于单位面积（25×25 或 ⌀25）控制' },
  { code: '\u25cb', zh: '圆度', en: 'Circularity', category: 'form',
    datumAllowed: false, mmcAllowed: false,
    note: '标在表面上，不得标在尺寸上' },
  { code: '\u232d', zh: '圆柱度', en: 'Cylindricity', category: 'form',
    datumAllowed: false, mmcAllowed: false,
    note: '复合控制，同时含圆度、直线度、锥度' },
  // ---- 轮廓 profile（需基准或可不需）
  { code: '\u2312', zh: '线轮廓度', en: 'Profile of a Line', category: 'profile',
    datumAllowed: true, mmcAllowed: false },
  { code: '\u2313', zh: '面轮廓度', en: 'Profile of a Surface', category: 'profile',
    datumAllowed: true, mmcAllowed: false },
  // ---- 方向 orientation
  { code: '\u2225', zh: '平行度', en: 'Parallelism', category: 'orientation',
    datumAllowed: true, mmcAllowed: true },
  { code: '\u22a5', zh: '垂直度', en: 'Perpendicularity', category: 'orientation',
    datumAllowed: true, mmcAllowed: true },
  { code: '\u2220', zh: '倾斜度', en: 'Angularity', category: 'orientation',
    datumAllowed: true, mmcAllowed: true },
  // ---- 位置 location
  { code: '\u2295', zh: '位置度', en: 'Position', category: 'location',
    datumAllowed: true, mmcAllowed: true,
    note: '⊕ = 圆内十字；公差值前常带 ⌀（圆柱形公差带）' },
  // ---- 跳动 runout
  { code: '\u2197', zh: '圆跳动', en: 'Circular Runout', category: 'runout',
    datumAllowed: true, mmcAllowed: false },
  // 全跳动在 Unicode 里没有单一码位（标准是双箭头），用两个 ↗ 连写表示，
  // 与图纸字形一致、也不影响识别词典的「单字符查表」逻辑。
  { code: '\u2197\u2197', zh: '全跳动', en: 'Total Runout', category: 'runout',
    datumAllowed: true, mmcAllowed: false },
  // ---- 2018 版已取消（旧图/GB 仍在使用，保留可选以便如实录入）
  { code: '\u25ce', zh: '同轴度', en: 'Coaxiality (Concentricity)', category: 'location',
    datumAllowed: true, mmcAllowed: false, deprecated: true,
    note: 'ASME Y14.5-2018 已删除，改由位置度/跳动控制；旧图与 GB/T 1182 仍常见' },
  { code: '\u232f', zh: '对称度', en: 'Symmetry', category: 'location',
    datumAllowed: true, mmcAllowed: false, deprecated: true,
    note: 'ASME Y14.5-2018 已删除；旧图与 GB/T 1182 仍常见（OCR 常退化成 ≡ 或 =）' },
];

/** 码位 → 定义。识别引擎可能产出旧码位（见 `GDT_LEGACY_SYMBOLS`）。 */
const BY_CODE: Record<string, GdtSym> = Object.fromEntries(
  GDT_SYMBOLS.map((s) => [s.code, s]));

/**
 * 识别引擎旧码位（`core_engine/config.GDT_SYMBOLS` 里的 Miscellaneous Technical 批）
 * → 本表的在册项。**只用于把历史/识别产出显示成正确中文名与字形**，不写回库。
 */
export const GDT_LEGACY_SYMBOLS: Record<string, GdtSym> = {
  '\u23e4': BY_CODE['\u23e4'],            // ⏤ 直线度
  '\u232e': BY_CODE['\u23e4'],            // ⌮ 引擎码位 → 直线度
  '\u2014': BY_CODE['\u23e4'],            // em dash（字体退化）
  '\u2013': BY_CODE['\u23e4'],            // en dash
  '\u2311': BY_CODE['\u25b1'],            // ⌑ 引擎码位 → 平面度
  '\u222d': BY_CODE['\u25b1'],            // ∭ 引擎码位 → 平面度
  '\u23e5': BY_CODE['\u25b1'],            // ⏥ Unicode 标准字形 → 平面度（v0.67 补）
  '\u2317': BY_CODE['\u25cb'],            // ⌗ 引擎码位 → 圆度
  '\u25cb': BY_CODE['\u25cb'],
  '\u232d': BY_CODE['\u232d'],            // ⌭ 圆柱度
  '\u2312': BY_CODE['\u2312'],            // ⌒ 线轮廓度
  '\u2313': BY_CODE['\u2313'],            // ⌓ 面轮廓度
  '\u2225': BY_CODE['\u2225'],            // ∥
  '\u22a5': BY_CODE['\u22a5'],            // ⊥
  '\u27c2': BY_CODE['\u22a5'],            // ⟂
  '\u2220': BY_CODE['\u2220'],            // ∠
  '\u2316': BY_CODE['\u2295'],            // ⌖ 引擎码位 → 位置度
  '\u2331': BY_CODE['\u2197'],            // ⌱ 引擎码位 → 圆跳动
  '\u2330': BY_CODE['\u2197\u2197'],      // ⌰ 引擎码位 → 全跳动
  '\u25ce': BY_CODE['\u25ce'],            // ◎ 同轴度
  '\u2318': BY_CODE['\u25ce'],            // ⌘ 引擎码位 → 同心度
  '\u232f': BY_CODE['\u232f'],            // ⌯ 对称度
  '\u2261': BY_CODE['\u232f'],            // ≡ 对称度（ASME 字形，OCR 常退化成 =）
};

/** 符号（本表字形或识别引擎旧码位）→ 中文名；认不出就原样返回，便于人工发现。 */
export function symbolToZh(sym: string | null | undefined): string {
  if (!sym) return '';
  return BY_CODE[sym]?.zh ?? GDT_LEGACY_SYMBOLS[sym]?.zh ?? sym;
}

/** 符号 → 展示字形（写库字符可能与旧数据不同，展示统一走本表字形）。 */
export function symbolGlyph(sym: string | null | undefined): string {
  if (!sym) return '';
  return BY_CODE[sym]?.code ?? GDT_LEGACY_SYMBOLS[sym]?.code ?? sym;
}

/**
 * 把**原名称里的修饰符后缀**接到新名称后面（v0.36 / L3-①）。
 *
 * 例：原名 `位置度(CZ)` 改成平面度 ⇒ `平面度(CZ)`；原名无后缀 ⇒ 原样返回。
 *
 * 为什么保留：后缀是公差框的**修饰符**（`(CZ)` / `CZ` / `Ⓜ`…），与「符号代表
 * 哪种几何特征」正交 —— 改符号只该改类型，不该顺手把人工标注的修饰符抹掉。
 * 后端 `feature_service.gdt_type_from_name()` 也按同样的规则剥掉后缀再比中文名，
 * 两侧口径一致（改这里必须同步改那边）。
 */
export function withModifierSuffix(zh: string, prev: string | null | undefined): string {
  const m = /(\([^)]*\)|[A-Za-z\u24b6-\u24e9]+)\s*$/.exec((prev ?? '').trim());
  return m ? zh + m[1] : zh;
}

/** 声明本行是否引用基准：形状公差不引用（ASME 图 6-1）。 */
export function symbolAllowsDatum(sym: string | null | undefined): boolean {
  if (!sym) return true;
  return BY_CODE[sym]?.datumAllowed ?? GDT_LEGACY_SYMBOLS[sym]?.datumAllowed ?? true;
}

export function symbolAllowsMmc(sym: string | null | undefined): boolean {
  if (!sym) return true;
  return BY_CODE[sym]?.mmcAllowed ?? GDT_LEGACY_SYMBOLS[sym]?.mmcAllowed ?? true;
}

// ================================================================ 特征名
/**
 * **存量英文特征名 → 中文**（兼容表，只服务界面）。
 *
 * 背景：识别引擎以前把 `features.characteristic` 写成英文（`Length` / `Angle` /
 * `Radius` / `Hole`…），而形位公差那条路写的是中文（`面轮廓度`）—— 同一列两种语言。
 * 用户 2026-10-04 要求「特征里面的 Length\angle 英文全部改为中文」。
 *
 * 落点分工（**两边不要互相抄**）：
 *   · **新产出**的中文名由后端 `backend/core_engine/characteristic_names.py` 唯一决定；
 *   · 本表**只**把**库里已存的英文旧值**翻给界面看 —— 因为它不改写库，
 *     所以生产库、备份库、别人机器上的旧数据打开也是中文。
 *   两者方向相反（一个 type→中文，一个英文→中文），不构成「同一语义两份实现」。
 *
 * 取值认不出就**原样返回**（用户手打的 `HOOD HINGE STRAP` 这类自定义名必须保留，
 * 不能静默吃掉）。
 */
const CHARACTERISTIC_ZH: Record<string, string> = {
  Length: '长度',
  'Length (Limit)': '长度（极限）',
  Hole: '孔',
  Diameter: '直径',
  Radius: '半径',
  Angle: '角度',
  Depth: '深度',
  Chamfer: '倒角',
  Thread: '螺纹',
  'Basic Dimension': '理论尺寸',
  Reference: '参考尺寸',
  Limit: '极限尺寸',
  Dimension: '尺寸',
};

/** 特征名 → 界面显示用中文名（已是中文/自定义名则原样返回）。 */
export function characteristicZh(v: string | null | undefined): string {
  const s = (v ?? '').trim();
  if (!s) return '';
  return CHARACTERISTIC_ZH[s] ?? s;
}

/** Y14.5-2018 修饰符（图 6-12）。Ⓜ/Ⓛ 用在尺寸与基准后，其余用在公差框内。 */
export const GDT_MODIFIERS = [
  { code: '\u24c2', zh: '最大实体要求 MMC' },   // Ⓜ
  { code: '\u24c1', zh: '最小实体要求 LMC' },   // Ⓛ
  { code: '\u24c5', zh: '延伸公差带' },         // Ⓟ
  { code: '\u24bb', zh: '自由状态' },           // Ⓕ
  { code: '\u24c9', zh: '相切平面' },           // Ⓣ
  { code: '\u24ca', zh: '非对称轮廓度' },       // Ⓤ
  { code: '\u24be', zh: '独立（不相关）' },     // Ⓘ
];

// ================================================================ 检测工具
/**
 * 检测工具候选（对应后端 `features.method`）—— **唯一出处**。
 *
 * ⚠️ 这里曾经有**两份实现**（`FeatureTable.tsx` 的私有常量 + `balloonStyle.ts` 的
 * `METHOD_OPTS_SHARED`）。常量表分叉后改一份另一份不跟 —— 正是「界面上明明改了、
 * 下拉里还是旧值」的温床。现在两边都从这里取（`balloonStyle.ts` 只做 re-export）。
 *
 * 取值就是**中文字符串**：后端 `method` 是自由文本、不是枚举，历史数据里出现过表外
 * 的工具名，`FxSelect` 必须原样兜底显示，**不能**静默改成第一项。
 *
 * v0.31.5 变动：新增 目视 / 针规 / 膜厚仪 / 扭力 / 推拉力；「塞规」→「塞尺」。
 * ⚠️ **只改了下拉候选**。识别引擎 `_infer_method` 在「孔径 + 配合公差」时仍推荐「塞规」
 *   （孔用量规在技术上是对的），历史数据里也可能有 —— 这些值在下拉里以原值兜底显示，
 *   不会被静默改写。要连识别推荐一起改，须先跑真实图纸回归。
 *
 * v0.60 变动：新增 **高度表 / 拉力计**。这两项是用户报的缺口 ——「下拉里根本没有高度表」。
 *
 * ★ 判据不是"工具有没有用"，而是"它能不能走到报告里"。测量报告模板（表单
 *   XK240122062）F 列有**数据验证**，只认 10 个规范名：
 *     卡尺 · 目视 · 千分尺 · CMM · 投影仪 · 针规 · 检具 · 高度表 · 膜厚仪 · 拉力计
 *   而本下拉是**唯一入口**（`FxSelect` 是受控 `<select>`，**不能手输**）⇒
 *   规范名不在这里出现，用户就永远选不到它，报告 F 列只能空着。
 *   经 `METHOD_MAP` 翻译后能落到模板的项，见 `tests/test_method_template_v060.py` 的契约。
 *
 * ⚠️ 已知仍未闭环（本轮未动，别误以为已解决）：**塞尺 / 粗糙度仪 / 扭力 / 推拉力**
 *   这 4 项在模板里没有对应规范名，选了会让报告 F 列留空 + 告警。
 *   「塞规」与它们不同 —— 它只是从下拉退出，历史数据与识别推荐仍会产出。
 */
export const METHOD_OPTS: string[] = [
  // 常规量具（**顺序保持稳定**：老用户是肌肉记忆，新工具一律往后追加）
  '卡尺', '千分尺', 'CMM', '投影仪', '螺纹规', '塞尺', '角度规', '粗糙度仪',
  // v0.31.5 新增：目视与专用检具
  '目视', '针规', '膜厚仪', '扭力', '推拉力',
  // v0.60 新增：测量报告模板 F 列下拉的规范名（原先选不到 ⇒ 报告只能空着）
  '高度表', '拉力计',
];

/**
 * 实体条件候选（公差值后 / 基准字母后）。
 * 取值与库里的历史数据一致（`gdt_features.material_condition` 实测存 `MMC`），
 * 不要再引入 `M`/`L` 那套短码 —— 两套写法会让同一份数据出现两种表示。
 */
export const MATERIAL_OPTS: { v: string; label: string }[] = [
  { v: '', label: '无' },
  { v: 'MMC', label: '\u24c2 最大实体 (MMC)' },
  { v: 'LMC', label: '\u24c1 最小实体 (LMC)' },
  { v: 'RFS', label: '独立 / RFS' },
];

export const DATUM_COND_OPTS: { v: string; label: string }[] = [
  { v: '', label: '无' },
  { v: 'MMC', label: '\u24c2 MMC' },
  { v: 'LMC', label: '\u24c1 LMC' },
  { v: 'RFS', label: 'RFS' },
];

/**
 * 特殊特性候选（v0.49）。
 *
 * 语义来源：AS9102 Form3 / PPAP 的「特殊特性」栏，对应 SIP（制程检验作业指导书）
 * 里「B 列 特殊特性」。**前端唯一出处** —— 后端只做 `String(8)` 自由文本 +
 * `Literal["CC","SC"]` 校验，不另建枚举表（避免"同一语义第二份实现"）。
 *
 * 形态与 `METHOD_OPTS` 一致（纯 `string[]`，`FxSelect` 直接消费）：
 * 首项 `'—'` 代表「无特殊特性」，提交时映射成 `null`（不是空串 —— 库里存量是
 * `NULL`，写空串会多出一种"空"的表示）。
 */
export const CC_SC_OPTS: string[] = ['—', 'CC', 'SC'];

// ================================================================ 公差文本
/**
 * 数字 → 标注文本：去掉浮点噪声（0.30000000000000004 → 0.3），整数不带小数点。
 * `toFixed(6)` 足够覆盖图纸精度（最高 0.0005），又远小于 1e21 的科学计数阈值。
 */
export function fmtNum(v: number): string {
  return String(Number(v.toFixed(6)));
}

/** 带符号偏差项：正数补 `+`，0 就写 `0`（不写 `-0`、不写 `+0`）。 */
function devItem(v: number): string {
  return v > 0 ? `+${fmtNum(v)}` : fmtNum(v);
}

/**
 * 上/下偏差 → 公差标注文本。**公差列与展开行的唯一口径。**
 *
 * 规则（对应图纸写法）：
 *   · 两侧都有值且互为相反数（上 +0.1 / 下 -0.1）→ `±0.1`
 *   · 两侧都为 0（或都留空）→ `0`（图纸上即「无公差」）
 *   · 上 +0.2 / 下 0   → `+0.2/0`     ← 单边（只加上偏差）
 *   · 上 0   / 下 -0.1 → `0/-0.1`     ← **单边（只加下偏差），用户问的就是这种**
 *   · 上 -0.1 / 下 -0.2 → `-0.1/-0.2` ← 极限偏差（同号）
 *   · 其余 → `+0.5/-0.2`
 *
 * **单边公差怎么输入**（用户 2026-10-03 提问）：只填有值的那一侧，另一侧**留空**即可 ——
 * 留空按 0 处理（不是「未填」，而是「偏差为 0」）。留空 = 清空该输入框。
 *
 * 注意 `lower_dev` 是**带符号的下偏差**（`lower_limit = nominal + lower_dev`，
 * 与解析器 dimensions.py:980 一致），所以这里直接原样显示，不再做取反。
 */
export function fmtDev(u: number | null | undefined, l: number | null | undefined): string {
  const hu = u != null, hl = l != null;
  if (!hu && !hl) return '';
  const a = hu ? Number(u) : 0;
  const b = hl ? Number(l) : 0;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '';
  if (a === 0 && b === 0) return '0';
  if (a > 0 && Math.abs(a + b) < 1e-9) return `±${fmtNum(a)}`;
  return `${devItem(a)}/${devItem(b)}`;
}

/** 公差列展示所需的字段子集（尺寸特征）。 */
export interface TolLike {
  kind: string;
  nominal?: number | null;
  upper_tol?: string | null;
  lower_tol?: string | null;
  upper_dev?: number | null;
  lower_dev?: number | null;
  tolerance_text?: string | null;
  gdt?: {
    zone: number | null;
    zone_is_diameter: boolean;
    uz_offset?: number | null;
  } | null;
}

/**
 * 公差带 + 非对称偏置量 → **图纸写法**文本（`0.6UZ0.4` / `Ø0.2`）。
 *
 * 与后端 `core_engine/parse/gdt.py: zone_text()` **同一口径**（两处必须同时覆盖）：
 * 识别期拼一次、用户在展开行改完「公差带 / 非对称 UZ」后服务端再拼一次
 * （写回 `features.tolerance_text`）。界面这里只负责显示。
 *
 * 顺序即图纸从左到右：`Ø` 前缀 → 公差值 → `UZ` 偏置量。
 */
export function gdtZoneText(zone: number | null | undefined,
                            uzOffset?: number | null,
                            isDiameter = false): string {
  if (zone == null) return '';
  return `${isDiameter ? 'Ø' : ''}${fmtNum(zone)}`
       + (uzOffset == null ? '' : `UZ${fmtNum(uzOffset)}`);
}

/**
 * 「公差」一列显示什么。**派生优先级**（用户 2026-10-03 反馈的核心修复点）：
 *
 *   尺寸行：结构化上下偏差 → 公差文本 → 识别原始上下公差 → —
 *   形位公差行：公差带 zone → 公差文本 → —
 *
 * 为什么偏差必须排第一：识别阶段会顺手塞一条 `tolerance_text`（实测库里是
 * `0.2/-0`、`0/-0` 这种带 `-0` 的粗糙文本），以前它排在最前，于是**用户在展开行
 * 改上下偏差，公差列纹丝不动** —— 看起来像改了不生效，其实是显示优先级写反。
 * 现在偏差是唯一权威：只要有一侧非空，公差列就跟着它走。
 */
export function toleranceDisplay(f: TolLike): string {
  if (f.kind === 'gdt') {
    // 形位公差的「公差」就是公差带；Ø 前缀由「要求」列承担，这里不重复。
    // 非对称公差带（UZ）必须带上 —— `0.6` 与 `0.6UZ0.4` 是两种不同的检验要求，
    // 少了 `UZ0.4` 现场就按对称公差带做，等于把一个关键要求从界面上抹掉。
    const z = gdtZoneText(f.gdt?.zone, f.gdt?.uz_offset);
    if (z) return z;
    return f.tolerance_text || '—';
  }
  const d = fmtDev(f.upper_dev, f.lower_dev);
  if (d) return d;
  if (f.tolerance_text) return f.tolerance_text;
  if (f.upper_tol || f.lower_tol) {
    return `${f.upper_tol ?? ''}/${f.lower_tol ?? ''}`;
  }
  return '—';
}

/** 单边公差判断：一侧有值、另一侧为 0/空。用于展开行给提示。 */
export function unilateralHint(u: number | null | undefined,
                               l: number | null | undefined): string {
  const hu = u != null, hl = l != null;
  if (!hu && !hl) return '';
  const a = hu ? Number(u) : 0;
  const b = hl ? Number(l) : 0;
  if (a === 0 && b !== 0) return '单边公差（只加下偏差）';
  if (b === 0 && a !== 0) return '单边公差（只加上偏差）';
  if (a === 0 && b === 0) return '无偏差';
  return '';
}

const NUM = '[-+]?\\d+(?:\\.\\d+)?';

/**
 * 公差文本 → 结构化 (上偏差, 下偏差)。**只认无歧义的写法**，认不出返回 null
 * （调用方据此把它当普通文本存，而不是猜一个可能错的偏差）。
 *
 * 认的写法：
 *   `±0.1` `+-0.1`        → (0.1, -0.1)      对称公差
 *   `+0.2/0` `0.2/0`      → (0.2, 0)         单边上偏差
 *   `0/-0.1`              → (0, -0.1)        单边下偏差
 *   `+0.5/-0.2`           → (0.5, -0.2)      一般双边
 *   `-0.1` `+0.1`         → (0, -0.1)/(0.1, 0)
 *   `+0.15+0.05`          → (0.15, 0.05)     图纸连写（同号偏差）
 *   `0`                   → (0, 0)
 *
 * **刻意不认**：单个无符号正数（`0.1`）—— 它既可能是 ±0.1 也可能是 0/+0.1，
 * 猜错就是把公差写反，宁可按文本原样保存，让用户按 `±0.1` / `0/-0.1` 写清。
 */
export function parseDev(raw: string): { u: number; l: number } | null {
  const t = (raw || '').trim().replace(/[／]/g, '/').replace(/\s+/g, '');
  if (!t) return null;
  if (/^0$/.test(t)) return { u: 0, l: 0 };
  // ±0.1 / +-0.1
  const sym = t.match(new RegExp(`^(?:±|\\+\\-|\\-\\+)(\\d+(?:\\.\\d+)?)$`));
  if (sym) {
    const v = Number(sym[1]);
    return Number.isFinite(v) ? { u: v, l: -v } : null;
  }
  // a/b  （两侧都可带符号）
  const pair = t.match(new RegExp(`^(${NUM})/(${NUM})$`));
  if (pair) {
    const a = Number(pair[1]), b = Number(pair[2]);
    return Number.isFinite(a) && Number.isFinite(b) ? { u: a, l: b } : null;
  }
  // 连写：+0.15+0.05 / -0.1-0.2（同一串里两个带符号数）
  const glued = t.match(new RegExp(`^([-+]\\d+(?:\\.\\d+)?)([-+]\\d+(?:\\.\\d+)?)$`));
  if (glued) {
    const a = Number(glued[1]), b = Number(glued[2]);
    return Number.isFinite(a) && Number.isFinite(b) ? { u: a, l: b } : null;
  }
  // 单个带符号数：+0.1 → 单边上偏差；-0.1 → 单边下偏差
  const one = t.match(new RegExp(`^([-+]\\d+(?:\\.\\d+)?)$`));
  if (one) {
    const v = Number(one[1]);
    if (!Number.isFinite(v)) return null;
    return v >= 0 ? { u: v, l: 0 } : { u: 0, l: v };
  }
  return null;
}

/** 数量前缀分隔符：`4*R5` / `4X5` / `4×5`。与解析器 `classify._strip_qty_prefix` 同族。 */
const QTY_SEP = '[*xX×]';
/** 名义值前可能带的符号（半径 / 直径）。**吞掉但不解释** ——
 *  「是不是直径」由 `is_diameter` 单独承载、「半径」由特征名承载；在这里顺手设值
 *  等于把语义藏进正则，正是「改了没反应」的温床。 */
const QTY_VAL_SYM = '[RrØ⌀Φφ]?';

/**
 * 「要求」列的**数量写法** → 结构化 `(数量, 名义值)`。认不出返回 null。
 *
 * 为什么需要它（2026-10-16，用户诉求）：图纸上写 `4X R5.0`，界面上只有名义值 `5`，
 * 「4 处」这个信息无处可写，用户试图直接在「要求」列敲 `4*R5` 却被数值校验拦下。
 * 数量的家本来就是 `features.qty`（列早就有、识别器也在写），缺的只是一个入口。
 *
 * 认的写法（空格随意）：
 *   `4*R5` `4XR5` `4×R5` `4xR5`   → { qty: 4, nominal: 5 }
 *   `4*5`  `4X5`                  → { qty: 4, nominal: 5 }
 *   `2X Ø8.5`                     → { qty: 2, nominal: 8.5 }
 *
 * **刻意不认**：
 *   · 没有分隔符的 `4 5` / `45` —— 分不清是「4 处的 5」还是一个数，猜错就是写歪数据；
 *   · `0X5` / `-1X5` —— 数量 < 1 无意义，当没认出，交回普通的数值校验去拒绝；
 *   · 单独 `R5` / `Ø5` —— 本函数的职责**只是数量前缀**，没有前缀就返回 null
 *     （`R5` 会被普通的数值校验拒掉，不会静默变成 `5`：那种「吃掉一个字符」
 *      的行为看起来像成功，实际半径/直径属性一个字都没写进去）。
 */
export function parseQtyNominal(raw: string): { qty: number; nominal: number } | null {
  const t = (raw || '').trim().replace(/\s+/g, '');
  if (!t) return null;
  const m = t.match(new RegExp(`^(\\d{1,3})${QTY_SEP}${QTY_VAL_SYM}(\\d+(?:\\.\\d+)?)$`));
  if (!m) return null;
  const qty = Number(m[1]);
  const nominal = Number(m[2]);
  if (!Number.isInteger(qty) || qty < 1) return null;
  if (!Number.isFinite(nominal)) return null;
  return { qty, nominal };
}
